import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import {
  CircleCheck,
  Landmark,
  Mail,
  MapPin,
  Phone,
  Send,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  RotateCcw,
  Clock
} from 'lucide-react'

import conselho1 from '../assets/img/conselho1.jpg'
import conselho3 from '../assets/img/conselho3.jpeg'
import parque1 from '../assets/img/parque1.jpeg'
import parque2 from '../assets/img/parque2.jpeg'

import { WaveDivider } from '../components/WaveDivider'
import { api } from '../services/api'
import { DocumentsSection } from './DocumentsSection'
import { ReportsSection } from './ReportsSection'


/*
const stagesData = [
  { stage: 1, title: 'Autorização pelo CGPPP', status: 'concluido', duration: '11/04/2025' },
  { stage: 2, title: 'Estudos Técnicos', status: 'em-andamento', duration: '1º Sem. 2025 e 2º Sem. 2026' },
  { stage: 3, title: 'Aprovação do CGPPP', status: 'a-iniciar', duration: '16/04/2026' },
  { stage: 4, title: 'Audiência Pública', status: 'a-iniciar', duration: '03/06/2026' },
  { stage: 5, title: 'Edital', status: 'a-iniciar', duration: 'em elaboração' },
  { stage: 6, title: 'Licitação', status: 'a-iniciar', duration: 'em elaboração' },
  { stage: 7, title: 'Contrato', status: 'a-iniciar', duration: 'em elaboração' },
]
*/
const carouselImages = [
  { src: parque1, alt: 'Vista do Parque Urbano de Porto Velho' },
  { src: conselho1, alt: 'Reunião do Conselho Gestor de Parcerias Público-Privadas (CGPPP/PVH)' },
  { src: conselho3, alt: 'Membros do Conselho Gestor em reunião' },
]

function useScrollAnimation(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(element)
        }
      },
      { threshold }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [threshold])

  return { ref, isVisible }
}

function HeroSection() {
  const { ref, isVisible } = useScrollAnimation(0.1)

  return (
    <section id="hero" className="relative min-h-[85vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={parque2}
          alt="Parque urbano de Porto Velho"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a2a]/90 via-[#2d6a4f]/80 to-[#2d6a4f]/70" />
      </div>

      <div ref={ref} className="container relative z-10 py-20 md:py-28">
        <div className={`max-w-2xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <Landmark size={14} className="text-[#FACC1F]" />
            <span className="text-white/90 text-xs font-medium">Prefeitura Municipal de Porto Velho</span>
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-white/90 leading-tight mb-4">
            Conselho Gestor de Parcerias Público-Privadas
          </h1>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#FACC1F] leading-tight mb-6">
            CGPPP - Concessão de Parques Urbanos
          </h2>
          <p className="text-white/90 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
            Portal de transparência do Conselho Gestor de Parcerias Público-Privadas. 
            Acompanhe os estudos e procedimentos para a concessão dos parques urbanos de Porto Velho.
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
            <a href="#documentos" className="inline-flex items-center gap-2 px-6 py-3 bg-[#51A64D] text-white font-semibold rounded-lg hover:bg-[#458f3f] transition-all shadow-lg">
              <FolderOpen size={18} />
              Documentos e Anexos
            </a>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { href: '#timeline', label: 'Fases' },
              { href: '#sobre', label: 'Sobre' },
              { href: '#documentos', label: 'Documentos' },
              { href: '#faq', label: 'FAQ' },
              { href: '#galeria', label: 'Galeria' },
              { href: '#contato', label: 'Contato' },
            ].map((item) => (
              <a key={item.href} href={item.href} className="px-3 py-1 bg-white/10 text-white/80 text-xs rounded-full hover:bg-white/20 transition-colors border border-white/20">
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function TimelineSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [stages, setStages] = useState<any[]>([
    { id: '1', stage: 1, title: 'Qualificação no CGPPP', description: 'Qualificação do projeto de concessão dos parques urbanos no Conselho Gestor.', status: 'concluido', duration: '11/04/2025' },
    { id: '2', stage: 2, title: 'Estudos Técnicos', description: 'Elaboração dos estudos técnicos preliminares, de viabilidade econômica, financeira e jurídica.', status: 'em-andamento', duration: '1º Sem. 2025 e 2º Sem. 2026' },
    { id: '3', stage: 3, title: 'Aprovação do CGPPP', description: 'Aprovação do projeto pelo Conselho Gestor.', status: 'a-iniciar', duration: '16/04/2026' },
    { id: '4', stage: 4, title: 'Audiência Pública', description: 'Realização de audiência pública para apresentação e debate do projeto.', status: 'a-iniciar', duration: '03/06/2026' },
    { id: '5', stage: 5, title: 'Edital', description: 'Publicação do edital de licitação.', status: 'a-iniciar', duration: 'em elaboração' },
    { id: '6', stage: 6, title: 'Licitação', description: 'Realização de certame licitatório.', status: 'a-iniciar', duration: 'em elaboração' },
    { id: '7', stage: 7, title: 'Contrato', description: 'Assinatura do contrato de concessão.', status: 'a-iniciar', duration: 'em elaboração' }
  ])
  const [isLoading, setIsLoading] = useState(false)

  const loadStages = async () => {
    setIsLoading(true)
    try {
      const data = await api.getAllStages()
      if (data && data.length > 0) {
        setStages(data)
      }
    } catch (error) {
      console.error('Erro ao carregar fases:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadStages()
    
    const handleStagesUpdate = () => {
      loadStages()
    }
    window.addEventListener('stages-updated', handleStagesUpdate)
    return () => window.removeEventListener('stages-updated', handleStagesUpdate)
  }, [])

  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    concluido: { bg: 'bg-[#51A64D]', text: 'text-white', label: 'Concluído' },
    'em-andamento': { bg: 'bg-[#FACC1F]', text: 'text-gray-900', label: 'Em andamento' },
    'a-iniciar': { bg: 'bg-gray-200', text: 'text-gray-600', label: 'A iniciar' }
  }

  if (isLoading && stages.length === 0) {
    return (
      <section id="timeline" className="py-16 md:py-20 bg-white">
        <div className="container text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mb-4"></div>
          <p className="text-gray-500">Carregando fases do projeto...</p>
        </div>
      </section>
    )
  }

  return (
    <section id="timeline" className="py-16 md:py-20 bg-white">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="text-center mb-12">
            <span className="inline-block text-[#1D71C3] text-xs font-semibold uppercase tracking-wider mb-2">Cronograma</span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Fases do Projeto</h2>
            <p className="text-gray-600 text-sm">Acompanhe o andamento de cada fase do projeto</p>
            {isLoading && <p className="text-xs text-gray-400 mt-1">Atualizando...</p>}
          </div>

          {/* LAYOUT HORIZONTAL - Grid responsivo */}
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-5 min-w-[800px] lg:grid lg:grid-cols-7">
              {stages.map((step) => {
                const config = statusConfig[step.status] || statusConfig['a-iniciar']
                const isEmAndamento = step.status === 'em-andamento'
                const isConcluido = step.status === 'concluido'
                
                return (
                  <div key={step.id} className="flex-1 min-w-[160px] bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all">
                    {/* Número da fase */}
                    <div className="flex justify-center mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-md ${
                        isEmAndamento ? 'bg-[#1D71C3] ring-4 ring-[#1D71C3]/20' :
                        isConcluido ? 'bg-[#51A64D]' : 'bg-gray-300'
                      }`}>
                        <span className="text-white font-bold text-sm">{step.stage}</span>
                      </div>
                    </div>
                    
                    {/* Título */}
                    <h3 className="font-serif font-bold text-sm text-gray-900 text-center mb-2 line-clamp-2">{step.title}</h3>
                    
                    {/* Duração/Período */}
                    <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-3">
                      <Clock size={12} />
                      <span className="truncate">{step.duration}</span>
                    </div>
                    
                    {/* Status */}
                    <div className="flex justify-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${config.bg} ${config.text}`}>
                        {isEmAndamento && <span className="w-1.5 h-1.5 bg-current rounded-full mr-1 animate-pulse" />}
                        {config.label}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Botão Atualizar */}
          <div className="flex justify-center mt-8">
            <button
              onClick={loadStages}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-[#1D71C3] hover:text-[#175FA3] transition-colors"
            >
              <RotateCcw size={14} />
              Atualizar fases
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

function AboutSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [aboutText, setAboutText] = useState({
    paragraph1: 'O Projeto tem por objeto parceria público-privada (PPP), na modalidade de concessão administrativa, para a prestação dos serviços públicos de apoio à visitação, gestão, operação, manutenção e conservação do Parque da Cidade, Parque Jardim das Mangueiras e Parque das Águas, bem como a execução de obras e serviços de engenharia, no município de Porto Velho – RO.',
    paragraph2: 'A proposta busca promover a revitalização, modernização, manutenção e ampliação da infraestrutura dos parques urbanos, garantindo mais qualidade nos espaços públicos, incentivo ao turismo, lazer, esporte, cultura e convivência social para a população.',
    paragraph3: 'Os estudos foram desenvolvidos por equipe multidisciplinar especializada, abrangendo análises operacionais, ambientais, econômico-financeiras e jurídicas, visando assegurar a viabilidade do projeto, a sustentabilidade dos serviços e a melhoria contínua dos espaços urbanos.',
    paragraph4: 'O projeto também prevê mecanismos de transparência, participação social e governança, permitindo que a população acompanhe as etapas de desenvolvimento e futura implementação da parceria público-privada.'
  })

  useEffect(() => {
    const loadAboutText = async () => {
      try {
        const response = await fetch('/api/public/settings/about_text')
        if (response.ok) {
          const data = await response.json()
          if (data.value) {
            setAboutText(data.value)
          }
        }
      } catch (error) {
        console.error('Erro ao carregar texto do sobre:', error)
      }
    }
    loadAboutText()
  }, [])

  return (
    <section id="sobre" className="py-16 md:py-20 bg-[#F5F7FA]">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="text-center mb-12">
            <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">Sobre o Projeto</span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Concessão dos Parques Urbanos de Porto Velho
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gray-700 leading-relaxed mb-4">{aboutText.paragraph1}</p>
              <p className="text-gray-700 leading-relaxed mb-4">{aboutText.paragraph2}</p>
              <p className="text-gray-700 leading-relaxed mb-4">{aboutText.paragraph3}</p>
              <p className="text-gray-700 leading-relaxed">{aboutText.paragraph4}</p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src={parque2} 
                alt="Parque Urbano de Porto Velho" 
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function GallerySection() {
  const { ref, isVisible } = useScrollAnimation()
  const [currentIndex, setCurrentIndex] = useState(0)

  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % carouselImages.length)
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)

  useEffect(() => {
    const interval = setInterval(nextImage, 5000)
    return () => clearInterval(interval)
  }, [])

  return (
    <section id="galeria" className="py-16 md:py-20 bg-white">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="text-center mb-10">
            <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">Multimídia</span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Galeria do Projeto</h2>
            <p className="text-gray-600">Imagens e registros do projeto de concessão dos parques urbanos.</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-xl overflow-hidden shadow-lg bg-gray-100">
              <div className="aspect-[16/9]">
                <img 
                  src={carouselImages[currentIndex].src} 
                  alt={carouselImages[currentIndex].alt} 
                  className="w-full h-full object-cover transition-opacity duration-500" 
                />
              </div>
              <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-all">
                <ChevronLeft size={24} />
              </button>
              <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-all">
                <ChevronRight size={24} />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm text-center">{carouselImages[currentIndex].alt}</p>
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-4">
              {carouselImages.map((_, index) => (
                <button 
                  key={index} 
                  onClick={() => setCurrentIndex(index)} 
                  className={`w-2 h-2 rounded-full transition-all ${currentIndex === index ? 'w-8 bg-[#51A64D]' : 'bg-gray-300'}`} 
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function FAQSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [faqs, setFaqs] = useState<any[]>([
    { id: '1', question: 'O que é o Programa de Parceria Público-Privada (PPP) de Porto Velho?', answer: 'O Programa de Parcerias Público-Privadas (PPP/PVH) de Porto Velho, instituído pela Lei Complementar nº 592/2015, é um mecanismo de interação entre a Prefeitura e a iniciativa privada para viabilizar projetos de infraestrutura e serviços públicos. O setor privado financia e opera serviços, enquanto o governo municipal garante a prestação à população.', order: 1, isActive: true },
    { id: '2', question: 'O que é o CGPPP/PVH?', answer: 'O CGPPP/PVH é o Conselho Gestor do Programa de Parceria Público-Privada do Município de Porto Velho. É o órgão responsável por conduzir os procedimentos relacionados às parcerias público-privadas, desde a fase inicial dos estudos até a conclusão dos certames licitatórios.', order: 2, isActive: true },
    { id: '3', question: 'Quais parques urbanos estão incluídos no projeto de concessão?', answer: 'O projeto contempla o Parque da Cidade, o Parque Jardim das Mangueiras (popularmente conhecido como "Skate Parque") e o Parques das Águas (oficialmente reconhecido como Área de Relevante Interesse Ecológico - ARIE Parque das Águas).', order: 3, isActive: true },
    { id: '4', question: 'Como posso acompanhar o andamento do projeto?', answer: 'Este portal foi criado especificamente para garantir transparência e publicidade ao projeto. Aqui você encontrará estudos técnicos de viabilidade econômica, financeira e jurídica, minutas e regulamentos, atas, comunicados oficiais e todas as atualizações sobre as etapas do projeto de concessão dos parques urbanos.', order: 4, isActive: true },
    { id: '5', question: 'Haverá audiência pública sobre o projeto?', answer: 'Sim. Estão previstas consultas e audiências públicas como parte do processo de transparência e participação social. Os avisos serão publicados neste portal e nos canais oficiais da Prefeitura de Porto Velho com antecedência.', order: 5, isActive: true },
    { id: '6', question: 'Como posso participar e contribuir com o projeto?', answer: 'A participação popular é fundamental. Você pode acompanhar este portal para informações sobre consultas públicas, audiências e outros canais de participação. Também é possível enviar sugestões e dúvidas através do formulário de contato disponível neste site.', order: 6, isActive: true },
    { id: '7', question: 'Qual é o modelo de referência utilizado para este projeto?', answer: 'O CGPPP/PVH utiliza como referência estrutural e organizacional o modelo adotado pelo Governo do Estado de São Paulo, no âmbito da Secretaria de Parcerias em Investimentos – SPI, especificamente o portal de projetos qualificados.', order: 7, isActive: true },
    { id: '8', question: 'Quais documentos estarão disponíveis neste portal?', answer: 'O portal disponibilizará: estudos técnicos preliminares, estudos de viabilidade econômica, financeira e jurídica, relatórios técnicos, minutas, regulamentos, avisos de consulta e audiência pública, atas, comunicados oficiais, cronogramas, atualizações das próximas etapas e documentos preparatórios vinculados aos futuros processos licitatórios.', order: 8, isActive: true },
    { id: '9', question: 'O acesso aos parques será cobrado após a concessão?', answer: 'O acesso aos parques serão totalmente gratuitos.', order: 9, isActive: true },
    { id: '10', question: 'Qual é o prazo previsto para conclusão do processo?', answer: 'O cronograma prevê a conclusão dos estudos técnicos no 1º semestre de 2026, consulta pública, audiência pública e publicação do edital de licitação no 2º semestre de 2026, e assinatura do contrato no 1º semestre de 2027. Esses prazos podem ser ajustados conforme o andamento do processo.', order: 10, isActive: true },
    { id: '11', question: 'PPP é a mesma coisa que privatização dos parques?', answer: 'Não. Na concessão via PPP, os parques continuam sendo bens públicos municipais. O parceiro privado é responsável pela gestão, manutenção e operação dos espaços por um período determinado, e ao final do contrato, tudo retorna ao Município nas condições pactuadas. Diferente da privatização, não há transferência definitiva de propriedade.', order: 11, isActive: true },
    { id: '12', question: 'Quem é o responsável pelo projeto?', answer: 'A Prefeitura de Porto Velho é a responsável pelo projeto, na condição de Poder Concedente, por meio do Conselho Gestor do Programa de Parcerias Público-Privadas (CGPPP/PVH). Os estudos técnicos, econômicos e jurídicos foram realizados pelo Consórcio Pezco-Apparecido-PSPHub.', order: 12, isActive: true }
  ])
  const [isLoading, setIsLoading] = useState(false)

  const loadFaqs = async () => {
    setIsLoading(true)
    try {
      const data = await api.getFaqs()
      if (data && data.length > 0) {
        const activeFaqs = data.filter(f => f.isActive).sort((a, b) => a.order - b.order)
        setFaqs(activeFaqs)
      }
    } catch (error) {
      console.error('Erro ao carregar FAQs:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadFaqs()
    
    const handleFaqsUpdate = () => {
      loadFaqs()
    }
    window.addEventListener('faqs-updated', handleFaqsUpdate)
    return () => window.removeEventListener('faqs-updated', handleFaqsUpdate)
  }, [])

  if (isLoading && faqs.length === 0) {
    return (
      <section id="faq" className="py-16 md:py-20 bg-[#F5F7FA]">
        <div className="container text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mb-4"></div>
          <p className="text-gray-500">Carregando perguntas frequentes...</p>
        </div>
      </section>
    )
  }

  return (
    <section id="faq" className="py-16 md:py-20 bg-[#F5F7FA]">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">Dúvidas Frequentes</span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Perguntas Frequentes</h2>
            <p className="text-gray-600">Encontre respostas para as principais dúvidas sobre o projeto de concessão dos parques urbanos.</p>
            {isLoading && <p className="text-xs text-gray-400 mt-1">Atualizando...</p>}
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {faqs.map((item) => (
                <details key={item.id} className="group bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                  <summary className="flex cursor-pointer list-none items-center justify-between p-5 font-semibold text-gray-900 hover:text-[#51A64D] transition-colors">
                    <span className="text-base">{item.question}</span>
                    <ChevronRight size={20} className="text-gray-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function ContatoSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({ nome: '', email: '', assunto: '', mensagem: '', consentimento: false })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.consentimento) {
      toast.error('Você precisa concordar com a Política de Privacidade.')
      return
    }
    if (!formData.nome.trim() || !formData.email.trim() || !formData.mensagem.trim()) {
      toast.error('Por favor, preencha os campos obrigatórios.')
      return
    }
    setIsSubmitting(true)
    try {
      await api.submitContact({ nome: formData.nome, email: formData.email, mensagem: formData.mensagem, consentimento: formData.consentimento })
      toast.success('Mensagem enviada com sucesso!')
      setSubmitted(true)
      setFormData({ nome: '', email: '', assunto: '', mensagem: '', consentimento: false })
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contato" className="py-16 md:py-20 bg-white">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">Fale Conosco</span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Entre em contato</h2>
            <p className="text-gray-600">Envie suas dúvidas, sugestões ou solicite informações sobre o projeto.</p>
          </div>
          <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-green-50 rounded-xl p-8 text-center border border-green-100">
                  <CircleCheck size={48} className="text-[#51A64D] mx-auto mb-4" />
                  <h3 className="font-bold text-xl mb-2">Mensagem enviada!</h3>
                  <p className="text-gray-500 text-sm mb-6">Agradecemos seu contato. Responderemos em breve.</p>
                  <button onClick={() => setSubmitted(false)} className="px-6 py-2 bg-[#51A64D] text-white rounded-lg hover:bg-[#458f3f]">Nova mensagem</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 space-y-5">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo</label><input type="text" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20" placeholder="Seu nome completo" required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">E-mail</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20" placeholder="seu@email.com" required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Assunto</label><input type="text" value={formData.assunto} onChange={(e) => setFormData({ ...formData, assunto: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20" placeholder="Assunto da mensagem" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Mensagem</label><textarea value={formData.mensagem} onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm min-h-[120px] focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20" placeholder="Escreva sua mensagem..." required /></div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <input type="checkbox" checked={formData.consentimento} onChange={(e) => setFormData({ ...formData, consentimento: e.target.checked })} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#51A64D] focus:ring-[#51A64D]" required />
                    <label className="text-xs text-gray-600 leading-relaxed">Li e concordo com a <strong>Política de Privacidade</strong>.</label>
                  </div>
                  <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-[#51A64D] text-white font-semibold rounded-lg hover:bg-[#458f3f] transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                    {isSubmitting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={16} />}
                    {isSubmitting ? 'Enviando...' : 'Enviar mensagem'}
                  </button>
                </form>
              )}
            </div>
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h3 className="font-serif font-bold text-sm text-gray-900 mb-3">Canais Institucionais</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3"><Mail size={16} className="text-[#51A64D] mt-0.5" /><div><div className="font-medium text-gray-900">E-mail</div><div className="text-gray-500 text-xs break-all">cgp.pvh@portovelho.ro.gov.br</div></div></div>
                  <div className="flex items-start gap-3"><Phone size={16} className="text-[#51A64D] mt-0.5" /><div><div className="font-medium text-gray-900">Telefone</div><div className="text-gray-500 text-xs">(69) 99204-0403</div></div></div>
                  <div className="flex items-start gap-3"><MapPin size={16} className="text-[#51A64D] mt-0.5" /><div><div className="font-medium text-gray-900">Endereço</div><div className="text-gray-500 text-xs">Prefeitura Municipal de Porto Velho – RO</div></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default function Home() {
  return (
    <main id="conteudo-principal">
      <HeroSection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <TimelineSection />
      <WaveDivider position="bottom" fillColor="#F5F7FA" />
      <AboutSection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <DocumentsSection />
      <WaveDivider position="bottom" fillColor="#F5F7FA" />
      <ReportsSection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <GallerySection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <FAQSection />
      <WaveDivider position="bottom" fillColor="#F5F7FA" />
      <ContatoSection />
    </main>
  )
}